import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import {
  useListVariantImages,
  useSetVariantMainImage,
} from '../../../../services/products'
import { VariantImageCard, AddImageCard } from './VariantImageCard'
import { Loader2 } from 'lucide-react'
import {
  uploadDocument,
  replaceDocument,
  deleteDocument,
} from '../../../../services/documentsApi'
import dialogConfirm from '../../../dialogConfirm'

interface ImagesTabProps {
  variantId: number
}

export function ImagesTab({ variantId }: ImagesTabProps) {
  const queryClient = useQueryClient()
  const { data, isLoading, error } = useListVariantImages({
    variant_id: variantId,
  })
  const setMainImageMutation = useSetVariantMainImage()
  const hasAutoSetMain = useRef(false)

  // Track mutation states for individual images
  const [changingImageId, setChangingImageId] = useState<number | null>(null)
  const [removingImageId, setRemovingImageId] = useState<number | null>(null)
  const [isUploading, setIsUploading] = useState(false)

  const rawImages = data?.results || []

  // Sort images so main image appears first
  const images = [...rawImages].sort((a, b) => {
    if (a.is_main && !b.is_main) return -1
    if (!a.is_main && b.is_main) return 1
    return 0
  })

  // Auto-set first image as main if no main image exists
  useEffect(() => {
    if (
      rawImages.length > 0 &&
      !rawImages.some((img) => img.is_main) &&
      !hasAutoSetMain.current &&
      !setMainImageMutation.isPending
    ) {
      hasAutoSetMain.current = true
      // Set the first image from the raw list (preserves backend ordering for new main)
      setMainImageMutation.mutate({
        variant_id: variantId,
        document_id: rawImages[0].id,
      })
    }
  }, [rawImages, variantId, setMainImageMutation])

  const invalidateQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['variant-images', variantId] })
    // Also invalidate document tables
    queryClient.invalidateQueries({
      predicate: (query) => {
        const firstKey = query.queryKey[0]
        return (
          typeof firstKey === 'string' &&
          (firstKey.startsWith('DOCUMENTS_') ||
            firstKey.startsWith('documents-modal-'))
        )
      },
    })
  }

  const handleSetMain = (documentId: number) => {
    setMainImageMutation.mutate({
      variant_id: variantId,
      document_id: documentId,
    })
  }

  const handleChangeImage = async (documentId: number, file: File) => {
    setChangingImageId(documentId)
    try {
      await replaceDocument(documentId, { file })
      toast.success('Image replaced successfully')
      invalidateQueries()
    } catch (error: any) {
      toast.error(error.message || 'Failed to replace image')
    } finally {
      setChangingImageId(null)
    }
  }

  const handleRemoveImage = async (documentId: number) => {
    try {
      await dialogConfirm(
        'Are you sure you want to delete this image?',
        'Delete Image'
      )
    } catch {
      return // User cancelled
    }

    setRemovingImageId(documentId)
    try {
      await deleteDocument(documentId)
      toast.success('Image deleted successfully')
      invalidateQueries()
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete image')
    } finally {
      setRemovingImageId(null)
    }
  }

  const handleAddImage = async (file: File) => {
    setIsUploading(true)
    try {
      await uploadDocument({
        file,
        variant_id: variantId,
      })
      toast.success('Image uploaded successfully')
      invalidateQueries()
    } catch (error: any) {
      toast.error(error.message || 'Failed to upload image')
    } finally {
      setIsUploading(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64 text-red-500">
        Error loading images. Please try again.
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Info banner about main image - only show when there are images */}
      {images.length > 0 && (
        <div className="bg-pistachio-200 border border-sage-md rounded-lg p-3 text-sm text-brand-active">
          <span className="font-medium">Tip:</span> Click the star icon on an
          image to set it as the main product image. The main image will be used
          on the spec sheet.
        </div>
      )}

      {/* Images grid */}
      <div className="flex flex-wrap gap-4">
        {images.map((image) => (
          <VariantImageCard
            key={image.id}
            image={image}
            onSetMain={handleSetMain}
            onChangeImage={handleChangeImage}
            onRemoveImage={handleRemoveImage}
            canSetMain={true}
            isSettingMain={setMainImageMutation.isPending}
            isChanging={changingImageId === image.id}
            isRemoving={removingImageId === image.id}
            disableActions={image.is_pim_asset}
          />
        ))}
        {/* Add Image card */}
        <AddImageCard onAddImage={handleAddImage} isUploading={isUploading} />
      </div>
    </div>
  )
}
